import { useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import CustomDatePicker from "@/components/CustomDatePicker";
import { tagButtonStyle } from "@/components/FinancialShared/tableStyles";
import {
  IBoletoCobrancaErroRetorno,
  IErroBmp,
  IGerarBoletosCobrancaRequest,
  IGerarBoletosCobrancaRetorno,
  IParcelaDetalhadaRetorno,
} from "@/models/consignado/proposal-billings.model";
import { gerarBoletosCobranca } from "@/services/consignado/proposta-cobranca.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { toYYYYMMDD } from "./dateYYYYMMDD";
import { formLabelProps, modalHeaderProps } from "./modalStyles";

// Modal "Gerar boleto" da seção Parcelas — REAL (TAR-045, backend
// his-adm-cobranca-020 `8fbfa26c`): POST /proposta/cobrancas/:ccb/boletos com as
// parcelas selecionadas numa chamada só (a BMP agrupa). Cada campo é um
// override opcional do default do servidor (liquidação total, vencimento D+5,
// expiração = vencimento) — o body só leva o que o operador mexeu. A validação
// daqui espelha o DTO do backend (required-when / mínimos).
//
// Forma de cobrança: Boleto e PIX como checkboxes independentes, ao menos um
// marcado, e o body SEMPRE manda via_boleto/via_pix explícitos. As combinações
// ainda estão em teste contra a BMP (16/09): os dois true devolveu CON_001
// ("PagamentoViaBoleto ou PagamentoViaPIX deve ser verdadeiro") e só PIX
// devolveu BMP_V0001_C0457 (falha ao registrar o Pix) — pode ser instabilidade.
// Decisão do time: deixar as opções abertas até testar um E outro e um OU
// outro; o padrão espelha o backend (`c878a530`): os dois marcados. Hoje o
// boleto da BMP vem só com código de barras.
//
// "Permitir descapitalização" começa desmarcado (default do backend é false)
// e só vai no body quando marcado — mesmo rótulo do Relatório de quitação.
//
// "Ignorar escrituração" fica disponível pra qualquer usuário da tela (o
// backend vai exigir só a role GRANATECH, a mesma que abre a tela — não é mais
// alçada de ADMIN). Exige motivo. Quando a BMP recusa por escrituração (422
// ESCRITURACAO_BLOQUEIA_COBRANCA, CON_002/CON_003) as mensagens ficam no modal,
// aberto, pro operador marcar a opção e reenviar.
//
// A resposta NÃO traz o boleto: ele entra em `parcelas[].boletos[]` do resumo
// financeiro quando a rotina de baixa (TAR-047) sincronizar (~90 s) — quem
// chama trata isso em `onSucesso`.

type Props = {
  numeroCcb: string;
  // Só as parcelas selecionadas (objetos, pra mostrar nº / vencimento / valor).
  parcelas: IParcelaDetalhadaRetorno[];
  isOpen: boolean;
  onClose: () => void;
  onSucesso: (retorno: IGerarBoletosCobrancaRetorno) => void;
};

type Erros = Partial<
  Record<"valorLiquidacao" | "valorDesconto" | "expiracao" | "via" | "motivo", string>
>;

const MOTIVO_MIN = 10;

// Motivos prontos pro `motivoIgnorarEscrituracao` (texto que vai à BMP como
// evidência). O 1º é o caso legítimo do comunicado da BMP de 14/08/2026.
// "Outro" abre o campo livre. Lista PROPOSTA — validar com a operação.
const MOTIVOS_IGNORAR_ESCRITURACAO = [
  "Empregador realizou a escrituração da parcela, porém não efetuou o desconto do cliente nem o pagamento da guia de FGTS.",
  "Empregador realizou a escrituração e o desconto do cliente, porém não efetuou o pagamento da guia de FGTS.",
  "Parcela em período de escrituração, mas o empregador informou que não fará o desconto nesta competência.",
] as const;
const MOTIVO_OUTRO = "OUTRO";

// Aceita vírgula (pt-BR) e ponto. "" → NaN.
const paraNumero = (texto: string) => Number(texto.trim().replace(",", "."));

export default function GerarBoletoModal({
  numeroCcb,
  parcelas,
  isOpen,
  onClose,
  onSucesso,
}: Props) {
  const toast = useToast();

  const [vencimento, setVencimento] = useState<[Date | null, Date | null]>([null, null]);
  const [expiracao, setExpiracao] = useState<[Date | null, Date | null]>([null, null]);
  const [liquidacaoTotal, setLiquidacaoTotal] = useState(true);
  const [valorLiquidacao, setValorLiquidacao] = useState("");
  const [valorDesconto, setValorDesconto] = useState("");
  const [permiteDescapitalizacao, setPermiteDescapitalizacao] = useState(false);
  const [viaBoleto, setViaBoleto] = useState(true);
  const [viaPix, setViaPix] = useState(true);
  const [descricao, setDescricao] = useState("");
  const [ignorarEscrituracao, setIgnorarEscrituracao] = useState(false);
  const [motivoSelecionado, setMotivoSelecionado] = useState("");
  const [motivoLivre, setMotivoLivre] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [erroEscrituracao, setErroEscrituracao] = useState<{
    message: string;
    erros: IErroBmp[];
  } | null>(null);

  const resetar = () => {
    setVencimento([null, null]);
    setExpiracao([null, null]);
    setLiquidacaoTotal(true);
    setValorLiquidacao("");
    setValorDesconto("");
    setPermiteDescapitalizacao(false);
    setViaBoleto(true);
    setViaPix(true);
    setDescricao("");
    setIgnorarEscrituracao(false);
    setMotivoSelecionado("");
    setMotivoLivre("");
    setSubmitted(false);
    setErroEscrituracao(null);
  };

  const gerar = useMutation({
    mutationFn: (body: IGerarBoletosCobrancaRequest) => gerarBoletosCobranca(numeroCcb, body),
    onSuccess(retorno) {
      onSucesso(retorno);
      resetar();
      gerar.reset();
      onClose();
    },
    onError(error: AxiosError<IBoletoCobrancaErroRetorno>) {
      const data = error.response?.data;
      if (data?.codigo === "ESCRITURACAO_BLOQUEIA_COBRANCA") {
        setErroEscrituracao({ message: data.message, erros: data.erros ?? [] });
        return;
      }
      toast({
        title: data?.codigo === "COBRANCA_ATIVA" ? "Cobrança ativa" : "Erro ao gerar boleto",
        description: data?.message || "Erro inesperado.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    },
  });

  const handleClose = () => {
    resetar();
    gerar.reset();
    onClose();
  };

  // Qualquer mudança de campo limpa o aviso da BMP (o operador vai reenviar).
  const aoMudar =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setErroEscrituracao(null);
      setter(v);
    };

  const validar = (): Erros => {
    const erros: Erros = {};
    if (!liquidacaoTotal) {
      const valor = paraNumero(valorLiquidacao);
      if (!valorLiquidacao.trim() || Number.isNaN(valor) || valor < 0.01) {
        erros.valorLiquidacao = "Informe o valor da liquidação (mínimo R$ 0,01).";
      }
    }
    if (liquidacaoTotal && valorDesconto.trim()) {
      const valor = paraNumero(valorDesconto);
      if (Number.isNaN(valor) || valor < 0) erros.valorDesconto = "Desconto inválido.";
    }
    if (vencimento[0] && expiracao[0] && expiracao[0] < vencimento[0]) {
      erros.expiracao = "A expiração não pode ser anterior ao vencimento.";
    }
    if (!viaBoleto && !viaPix) erros.via = "Marque ao menos uma forma: Boleto ou PIX.";
    if (ignorarEscrituracao) {
      if (!motivoSelecionado) {
        erros.motivo = "Selecione o motivo.";
      } else if (motivoSelecionado === MOTIVO_OUTRO && motivoLivre.trim().length < MOTIVO_MIN) {
        erros.motivo = `O motivo precisa explicar o caso, com ao menos ${MOTIVO_MIN} caracteres.`;
      }
    }
    return erros;
  };

  const erros = submitted ? validar() : {};
  const numerosParcelas = parcelas
    .map((p) => p.numero_parcela)
    .filter((n): n is number => n != null)
    .sort((a, b) => a - b);

  const montarBody = (): IGerarBoletosCobrancaRequest => {
    const body: IGerarBoletosCobrancaRequest = {
      parcelas: numerosParcelas,
      via_boleto: viaBoleto,
      via_pix: viaPix,
    };
    if (vencimento[0]) body.data_vencimento = toYYYYMMDD(vencimento[0]);
    if (expiracao[0]) body.data_expiracao = toYYYYMMDD(expiracao[0]);
    if (!liquidacaoTotal) {
      body.liquidacao_total = false;
      body.valor_liquidacao = paraNumero(valorLiquidacao);
    }
    if (liquidacaoTotal && valorDesconto.trim()) body.valor_desconto = paraNumero(valorDesconto);
    if (permiteDescapitalizacao) body.permite_descapitalizacao = true;
    if (descricao.trim()) body.descricao = descricao.trim();
    if (ignorarEscrituracao) {
      body.ignorar_escrituracao = true;
      body.motivo_ignorar_escrituracao =
        motivoSelecionado === MOTIVO_OUTRO ? motivoLivre.trim() : motivoSelecionado;
    }
    return body;
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (numerosParcelas.length === 0 || Object.keys(validar()).length > 0) return;
    gerar.mutate(montarBody());
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="lg" boxShadow="2xl">
        <ModalHeader {...modalHeaderProps}>Gerar boleto — CCB {numeroCcb}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={6}>
          <Flex direction="column" gap={5} fontFamily="Lexend Deca">
            <Box>
              <Text {...formLabelProps}>
                {numerosParcelas.length === 1
                  ? "Parcela selecionada"
                  : `${numerosParcelas.length} parcelas selecionadas`}
              </Text>
              {numerosParcelas.length === 0 ? (
                <Text fontSize="sm" color="red.500">
                  Nenhuma parcela selecionada.
                </Text>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th fontFamily="Lexend Deca">Parcela</Th>
                      <Th fontFamily="Lexend Deca">Vencimento</Th>
                      <Th fontFamily="Lexend Deca" isNumeric>
                        Saldo em aberto
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parcelas.map((p) => (
                      <Tr key={p.id}>
                        <Td fontSize="sm">{p.numero_parcela ?? "—"}</Td>
                        <Td fontSize="sm">
                          {p.vencimento ? formatDateWithNoHours(p.vencimento) : "—"}
                        </Td>
                        <Td fontSize="sm" isNumeric>
                          {BrlCurrencyFormatter(p.saldo_atual)}
                          {p.saldo_atual !== p.valor && (
                            <Text fontSize="xs" color="gray.500">
                              Parcela: {BrlCurrencyFormatter(p.valor)}
                            </Text>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
              <Text fontSize="xs" color="gray.500" mt={2}>
                As parcelas vão numa cobrança só; a bancarizadora decide o agrupamento e
                calcula o valor final sobre o saldo em aberto, com encargos de atraso.
              </Text>
            </Box>

            <Flex gap={4} direction={{ base: "column", md: "row" }}>
              <FormControl>
                <FormLabel {...formLabelProps}>Vencimento</FormLabel>
                <CustomDatePicker
                  single
                  compact
                  dateRange={vencimento}
                  onChange={aoMudar(setVencimento)}
                />
                <FormHelperText fontSize="xs">Sem data, a bancarizadora usa D+5.</FormHelperText>
              </FormControl>
              <FormControl isInvalid={Boolean(erros.expiracao)}>
                <FormLabel {...formLabelProps}>Expiração</FormLabel>
                <CustomDatePicker
                  single
                  compact
                  dateRange={expiracao}
                  onChange={aoMudar(setExpiracao)}
                  minDate={vencimento[0] ?? undefined}
                />
                {erros.expiracao ? (
                  <FormErrorMessage fontSize="xs">{erros.expiracao}</FormErrorMessage>
                ) : (
                  <FormHelperText fontSize="xs">Sem data, expira no vencimento.</FormHelperText>
                )}
              </FormControl>
            </Flex>

            <FormControl display="flex" alignItems="center" gap={3}>
              <Switch
                id="liquidacao-total"
                colorScheme="green"
                isChecked={liquidacaoTotal}
                onChange={(e) => {
                  aoMudar(setLiquidacaoTotal)(e.target.checked);
                  if (!e.target.checked) setValorDesconto("");
                }}
              />
              <FormLabel htmlFor="liquidacao-total" {...formLabelProps} mb={0}>
                Liquidação total
              </FormLabel>
            </FormControl>

            <Flex gap={4} direction={{ base: "column", md: "row" }}>
              {!liquidacaoTotal ? (
                <FormControl isRequired isInvalid={Boolean(erros.valorLiquidacao)}>
                  <FormLabel {...formLabelProps}>Valor da liquidação</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftAddon>R$</InputLeftAddon>
                    <NumberInput
                      w="100%"
                      min={0.01}
                      precision={2}
                      step={0.01}
                      value={valorLiquidacao}
                      onChange={(texto) => aoMudar(setValorLiquidacao)(texto)}
                      parse={(v) => v.replace(",", ".")}
                    >
                      <NumberInputField borderLeftRadius={0} />
                    </NumberInput>
                  </InputGroup>
                  <FormErrorMessage fontSize="xs">{erros.valorLiquidacao}</FormErrorMessage>
                </FormControl>
              ) : (
                <FormControl isInvalid={Boolean(erros.valorDesconto)}>
                  <FormLabel {...formLabelProps}>Desconto</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftAddon>R$</InputLeftAddon>
                    <NumberInput
                      w="100%"
                      min={0}
                      precision={2}
                      step={0.01}
                      value={valorDesconto}
                      onChange={(texto) => aoMudar(setValorDesconto)(texto)}
                      parse={(v) => v.replace(",", ".")}
                    >
                      <NumberInputField borderLeftRadius={0} />
                    </NumberInput>
                  </InputGroup>
                  {erros.valorDesconto ? (
                    <FormErrorMessage fontSize="xs">{erros.valorDesconto}</FormErrorMessage>
                  ) : (
                    <FormHelperText fontSize="xs">Opcional.</FormHelperText>
                  )}
                </FormControl>
              )}
            </Flex>

            <FormControl>
              <Checkbox
                colorScheme="green"
                isChecked={permiteDescapitalizacao}
                onChange={(e) => aoMudar(setPermiteDescapitalizacao)(e.target.checked)}
              >
                <Text fontSize="sm" fontWeight="500">
                  Permitir descapitalização
                </Text>
              </Checkbox>
              <FormHelperText fontSize="xs" mt={1}>
                Aplica descapitalização e encargos no cálculo da bancarizadora.
              </FormHelperText>
            </FormControl>

            <FormControl isInvalid={Boolean(erros.via)}>
              <FormLabel {...formLabelProps}>Forma de cobrança</FormLabel>
              <Flex gap={6}>
                <Checkbox
                  colorScheme="green"
                  isChecked={viaBoleto}
                  onChange={(e) => aoMudar(setViaBoleto)(e.target.checked)}
                >
                  <Text fontSize="sm">Boleto</Text>
                </Checkbox>
                <Checkbox
                  colorScheme="green"
                  isChecked={viaPix}
                  onChange={(e) => aoMudar(setViaPix)(e.target.checked)}
                >
                  <Text fontSize="sm">PIX</Text>
                </Checkbox>
              </Flex>
              <FormErrorMessage fontSize="xs">{erros.via}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel {...formLabelProps}>Descrição</FormLabel>
              <Input
                size="sm"
                value={descricao}
                onChange={(e) => aoMudar(setDescricao)(e.target.value)}
                placeholder="Opcional — texto que acompanha a cobrança"
              />
            </FormControl>

            {erroEscrituracao && (
              <Alert status="error" borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <Box fontSize="sm">
                  <AlertTitle fontSize="sm">A bancarizadora bloqueou a cobrança</AlertTitle>
                  <AlertDescription>
                    <Text>{erroEscrituracao.message}</Text>
                    {erroEscrituracao.erros.length > 0 && (
                      <UnorderedList mt={2} spacing={1}>
                        {erroEscrituracao.erros.map((erro, i) => (
                          <ListItem key={`${erro.codigo}-${i}`}>
                            <Text as="span" fontWeight="600">
                              {erro.codigo}
                            </Text>
                            : {erro.mensagem}
                          </ListItem>
                        ))}
                      </UnorderedList>
                    )}
                    <Text mt={2} fontSize="xs" color="gray.700">
                      Você pode marcar &quot;Ignorar escrituração&quot;, informar o motivo e reenviar.
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            <Box
              borderWidth="1px"
              borderColor="orange.200"
              bg="orange.50"
              borderRadius="md"
              p={3}
            >
              <Checkbox
                colorScheme="orange"
                isChecked={ignorarEscrituracao}
                onChange={(e) => aoMudar(setIgnorarEscrituracao)(e.target.checked)}
              >
                <Text fontSize="sm" fontWeight="500">
                  Ignorar escrituração
                </Text>
              </Checkbox>
              <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                Pede à bancarizadora que gere a cobrança mesmo com a parcela escriturada ou em
                período de escrituração. Fica registrado com seu usuário e o motivo.
              </Text>
              {ignorarEscrituracao && (
                <FormControl isRequired isInvalid={Boolean(erros.motivo)} mt={3}>
                  <FormLabel {...formLabelProps}>Motivo</FormLabel>
                  <Select
                    size="sm"
                    bg="white"
                    value={motivoSelecionado}
                    onChange={(e) => aoMudar(setMotivoSelecionado)(e.target.value)}
                    placeholder="Selecione o motivo"
                  >
                    {MOTIVOS_IGNORAR_ESCRITURACAO.map((texto) => (
                      <option key={texto} value={texto}>
                        {texto}
                      </option>
                    ))}
                    <option value={MOTIVO_OUTRO}>Outro (descrever)</option>
                  </Select>
                  {motivoSelecionado === MOTIVO_OUTRO && (
                    <Textarea
                      mt={2}
                      size="sm"
                      bg="white"
                      value={motivoLivre}
                      onChange={(e) => aoMudar(setMotivoLivre)(e.target.value)}
                      placeholder="Explique por que a escrituração deve ser ignorada"
                      rows={3}
                    />
                  )}
                  {erros.motivo ? (
                    <FormErrorMessage fontSize="xs">{erros.motivo}</FormErrorMessage>
                  ) : (
                    <FormHelperText fontSize="xs">
                      O texto vai à bancarizadora e fica registrado como evidência.
                      {motivoSelecionado === MOTIVO_OUTRO
                        ? ` Mínimo ${MOTIVO_MIN} caracteres.`
                        : ""}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter gap={3} borderTopWidth="1px" borderTopColor="gray.200" pt={4}>
          <Button
            variant="ghost"
            fontFamily="Lexend Deca"
            onClick={handleClose}
            isDisabled={gerar.isPending}
          >
            Cancelar
          </Button>
          <Button
            {...tagButtonStyle("green")}
            fontFamily="Lexend Deca"
            onClick={handleSubmit}
            isLoading={gerar.isPending}
            loadingText="Gerando..."
            isDisabled={numerosParcelas.length === 0}
          >
            Gerar boleto
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
